import { useAtomValue } from "jotai";
import { DetailTableRow } from "../../components/DetailTable";
import { useColorizedValue } from "../useColorizedValue";
import { incomeStatementMetricsAtom } from "../../metrics.state";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { getLast12Months } from "@/utils/metrics/metricsUtils";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { getMetric } from "../../utils/getMetric";
import { scrollAndHighlightRow } from "../../utils/scrollAndHighlight";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { useMetricClickHandler } from "../shared/useMetricClickHandler";
import { createMetricLabel } from "../../utils/labelHelpers";

export function useIncomeStatementData(): DetailTableRow[] {
    const incomeStatementMetrics = useAtomValue(incomeStatementMetricsAtom);
    const flags = useFlags();
    const showM05 = flags.enabled("show-im-m05");
    const handleMetricClick = useMetricClickHandler();

    const revenue = getMetric(
        incomeStatementMetrics,
        AccountLabel.REVENUES,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
    );
    const ebitda = getMetric(
        incomeStatementMetrics,
        AccountLabel.EBITDA,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
    );
    const ebitdaMargin = getMetric(
        incomeStatementMetrics,
        AccountLabel.EBITDA_MARGIN,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P,
    );
    const grossMargin = getMetric(
        incomeStatementMetrics,
        AccountLabel.GROSS_MARGIN,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P,
    );
    const netIncome = getMetric(
        incomeStatementMetrics,
        AccountLabel.NET_INCOME,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
    );
    const netIncomeMargin = getMetric(
        incomeStatementMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P,
    );
    const salesAndMarketingEfficiency = getMetric(
        incomeStatementMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
    );

    const revenueColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(revenue?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        revenue?.source,
        revenue?.info.derived.label,
    );

    const ebitdaColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(ebitda?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        ebitda?.source,
        ebitda?.info.derived.label,
    );

    const ebitdaMarginColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(ebitdaMargin?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        ebitdaMargin?.source,
        ebitdaMargin?.info.derived.label,
    );

    const grossMarginColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(grossMargin?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        grossMargin?.source,
        grossMargin?.info.derived.label,
    );

    const netIncomeColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(netIncome?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        netIncome?.source,
        netIncome?.info.derived.label,
    );

    const netIncomeMarginColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(netIncomeMargin?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        netIncomeMargin?.source,
        netIncomeMargin?.info.derived.label,
    );

    const salesAndMarketingEfficiencyColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(salesAndMarketingEfficiency?.values),
            FinanceLabFieldType.MULTIPLE,
        ) || "-",
        salesAndMarketingEfficiency?.source,
        salesAndMarketingEfficiency?.info.derived.label,
    );

    return [
        {
            label: createMetricLabel(
                "Revenue",
                revenue?.info.derived.localLabel,
            ),
            value: revenueColorized.value,
            valueClassName: revenueColorized.className,
            source: revenueColorized.source,
            sourceClassName: revenueColorized.sourceClassName,
            delta: revenue?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "Revenue",
                      )
                    : handleMetricClick(revenue, "Revenue"),
        },
        {
            label: createMetricLabel(
                "Sales & Marketing Efficiency",
                salesAndMarketingEfficiency?.info.derived.localLabel,
            ),
            value: salesAndMarketingEfficiencyColorized.value,
            valueClassName: salesAndMarketingEfficiencyColorized.className,
            source: salesAndMarketingEfficiencyColorized.source,
            sourceClassName:
                salesAndMarketingEfficiencyColorized.sourceClassName,
            delta: salesAndMarketingEfficiency?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "Sales & Marketing Efficiency",
                      )
                    : handleMetricClick(
                          salesAndMarketingEfficiency,
                          "Sales & Marketing Efficiency",
                      ),
        },
        {
            label: createMetricLabel(
                "Gross Margin %",
                grossMargin?.info.derived.localLabel,
            ),
            value: grossMarginColorized.value,
            valueClassName: grossMarginColorized.className,
            source: grossMarginColorized.source,
            sourceClassName: grossMarginColorized.sourceClassName,
            delta: grossMargin?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "Gross Margin %",
                      )
                    : handleMetricClick(grossMargin, "Gross Margin %"),
        },
        {
            label: createMetricLabel(
                "Net Income",
                netIncome?.info.derived.localLabel,
            ),
            value: netIncomeColorized.value,
            valueClassName: netIncomeColorized.className,
            source: netIncomeColorized.source,
            sourceClassName: netIncomeColorized.sourceClassName,
            delta: netIncome?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "Net Income",
                      )
                    : handleMetricClick(netIncome, "Net Income"),
        },
        {
            label: createMetricLabel("EBITDA", ebitda?.info.derived.localLabel),
            value: ebitdaColorized.value,
            valueClassName: ebitdaColorized.className,
            source: ebitdaColorized.source,
            sourceClassName: ebitdaColorized.sourceClassName,
            delta: ebitda?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "EBITDA",
                      )
                    : handleMetricClick(ebitda, "EBITDA"),
        },
        {
            label: createMetricLabel(
                "Net Income Margin %",
                netIncomeMargin?.info.derived.localLabel,
            ),
            value: netIncomeMarginColorized.value,
            valueClassName: netIncomeMarginColorized.className,
            source: netIncomeMarginColorized.source,
            sourceClassName: netIncomeMarginColorized.sourceClassName,
            delta: netIncomeMargin?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "Net Income Margin %",
                      )
                    : handleMetricClick(netIncomeMargin, "Net Income Margin %"),
        },
        {
            label: createMetricLabel(
                "EBITDA Margin %",
                ebitdaMargin?.info.derived.localLabel,
            ),
            value: ebitdaMarginColorized.value,
            valueClassName: ebitdaMarginColorized.className,
            source: ebitdaMarginColorized.source,
            sourceClassName: ebitdaMarginColorized.sourceClassName,
            delta: ebitdaMargin?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-income-statement",
                          "EBITDA Margin %",
                      )
                    : handleMetricClick(ebitdaMargin, "EBITDA Margin %"),
        },
    ];
}
